import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../CSS/Global.css';
import './CustomerStyle.css';
import Header from '../Header';
import Sidebar from '../SideBar';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import { BadgeCheck, BadgeIndianRupee, Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Mail, MapPin, Phone, Receipt } from 'lucide-react';
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { decryptToken } from '../utils/getToken';
import { jsonToXlsxBlob, triggerDownload } from '../../utils/excelSheet';
import { admissionStatusLabel, admissionStatusRowClass, formatAdmissionDateTimeIST } from './admissionFormat';

/** Status values available in header filter (matches admission API / row dropdown). */
const HEADER_STATUS_FILTERS = ['Active', 'Deactive', 'Paid', 'Failed'];

const ADMISSION_SAMPLE_ROW = {
  "Admission ID": "",
  "Payment ID": "",
  "Name": "",
  "Father Name": "",
  "Mother Name": "",
  "Date of Birth": "",
  "Aadhar": "",
  "Gender": "",
  "School": "",
  "Address": "",
  "Pincode": "",
  "Mobile": "",
  "Email": "",
  "Center": "",
  "Status": "",
  "Rules Agreed": "",
  "Created At": "",
};

const Admission = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  /** 'all' | admission API status — quick filters from header dropdown */
  const [statusFilter, setStatusFilter] = useState('all');
  const [isImportOpen, setImportOpen] = useState(false);
  const admissionFilterRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const url = API_BASE_URL
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
  const [load, setLoad] = useState(false)
   const token = decryptToken("accessToken") || ""

  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchCustomers = async () => {
    if (!isLoggedIn?.main_id) {
      setCustomers([]);
      return;
    }
    try {
      setLoad(true); // Start loading
      const response = await fetch(`${url}/admission/alladmin/${isLoggedIn.main_id}`, {
        headers: authHeaders,
      });
      const data = await response.json();
      // const parsedCustomers = data.map((customer) => ({
      //   ...customer,
      //   billingAddress: customer.billingAddress ? JSON.parse(customer.billingAddress) : {},
      //   shippingAddress: customer.shippingAddress ? JSON.parse(customer.shippingAddress) : {},
      //   contact: customer.contact ? JSON.parse(customer.contact) : [],
      // }));
      console.log("Customer data", data);
      const rows = Array.isArray(data?.user) ? data.user : [];
      setCustomers(rows);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setLoad(false); // Stop loading after everything is done
    }
  };


  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const onDown = (e) => {
      if (admissionFilterRef.current && !admissionFilterRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isDropdownOpen]);

  // 1. Filter by date (API may return non-array on error — always treat as array)
  const list = Array.isArray(customers) ? customers : [];
  let processedCustomers = [...list];

  console.log(processedCustomers)
  if (customDate.from || customDate.to) {
    processedCustomers = processedCustomers.filter((customer) => {
      const createdAt = new Date(customer.createdAt);
      const from = customDate.from ? new Date(customDate.from) : null;
      const to = customDate.to ? new Date(customDate.to) : null;

      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  if (HEADER_STATUS_FILTERS.includes(statusFilter)) {
    processedCustomers = processedCustomers.filter((c) => c.status === statusFilter);
  }

  // 2. Sort by createdAt
  processedCustomers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });


  const q = globalSearch.toLowerCase();
  const filteredCustomers = processedCustomers.filter((row) =>
    (row.name || "").toLowerCase().includes(q) ||
    (row.fatherName || "").toLowerCase().includes(q) ||
    (row.motherName || "").toLowerCase().includes(q) ||
    (row.email || "").toLowerCase().includes(q) ||
    (row.mobile || "").toLowerCase().includes(q) ||
    (row.admission_id || "").toLowerCase().includes(q) ||
    (row.payment_id || "").toLowerCase().includes(q) ||
    (row.center || "").toLowerCase().includes(q) ||
    (row.batch?.name || "").toLowerCase().includes(q) ||
    (row.school || "").toLowerCase().includes(q) ||
    (row.status || "").toLowerCase().includes(q)
  );

  const indexOfLastCustomer = currentPage * entriesPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleSearch = (event) => setSearchQuery(event.target.value);
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);

  const selectStatusFilter = (next) => {
    setStatusFilter(next);
    setDropdownOpen(false);
    setCurrentPage(1);
  };

  const admissionHeaderTitle =
    statusFilter === 'all' ? 'All admissions' : admissionStatusLabel(statusFilter);
  const toggleImport = () => setImportOpen(!isImportOpen);

  const handleDownloadAdmissionSample = async () => {
    const blob = await jsonToXlsxBlob([ADMISSION_SAMPLE_ROW], "Admissions");
    triggerDownload(blob, "admission_import_sample.xlsx");
    setImportOpen(false);
  };

  const handleDelete = async (customerId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This customer will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${url}/admission/${customerId}`, {
            method: 'DELETE',
            headers:{
              "Content-Type":"application/json",
              "Authorization":`Bearer ${token}`
            }
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err?.message || 'Failed to delete');
          }

          Swal.fire('Deleted!', 'Admission has been deleted.', 'success');
          setCustomers((prev) => (Array.isArray(prev) ? prev.filter((c) => c.id !== customerId) : []));
        } catch (error) {
          console.error('Delete error:', error.message);
          Swal.fire('Error!', `Failed to delete customer. ${error.message}`, 'error');
        }
      }
    });
  };


  const handleEdit = (customer) => {
    navigate('/edit-admission', { state: customer });

  };

  const handleStatusChange = async (e, customer) => {
    e.stopPropagation();
    const newStatus = e.target.value;

    // Optimistic UI update
    setCustomers(prev =>
      prev.map(c =>
        c.id === customer.id ? { ...c, status: newStatus } : c
      )
    );

    try {
      const res = await fetch(`${url}/admission/status/${customer.id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        throw new Error("Failed to update status");
      }

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Status updated successfully",
        timer: 1200,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error(error);

      // Rollback on error
      setCustomers(prev =>
        prev.map(c =>
          c.id === customer.id ? { ...c, status: customer.status } : c
        )
      );

      Swal.fire("Error", "Failed to update status", "error");
    }
  };


  // const handleDelete=()=>{

  // }


  const SkeletonLoader = () => (
    <div>
      {Array(10).fill('').map((_, index) => (
        <div className="skeleton-row" key={index}>
          {[...Array(5)].map((_, i) => <div className="skeleton" key={i}></div>)}
        </div>
      ))}
    </div>
  );

  const renderStatusRow = (customer) => {
    if (!customer.status) return null;
    const sc = admissionStatusRowClass(customer.status);
    const rowCls = sc ? `ecom-customer-row${sc}` : "ecom-customer-row";
    const iconCls = sc ? `ecom-customer-icon${sc}` : "ecom-customer-icon";
    return (
      <div className={rowCls}>
        <BadgeIndianRupee className={iconCls} strokeWidth={2} />
        <span>{admissionStatusLabel(customer.status)}</span>
      </div>
    );
  };

  if (load) {
    return (
      <Loader />
    );
  }

  return (
    <>

      <div className="admission-page">
        <div className="customers-header admission-page__toolbar">
          <div className="customer-dropdown admission-status-filter-anchor" ref={admissionFilterRef}>
            <p className="dashboard-title" onClick={toggleDropdown}>
              {admissionHeaderTitle}{' '}
              <i className={`fa-solid fa-chevron-down ${isDropdownOpen ? 'rotate' : ''}`}></i>
            </p>
            <div className="custom-header-ul">
              {isDropdownOpen && (
                <div className="customer-dropdown-list admission-status-filter-dropdown">
                  <div className="customer-dropdown-list-default">
                    <p className="default-filter">
                      <i className="fa-solid fa-chevron-right"></i>
                      Filter by status
                    </p>
                    <ul className="customer-list-ul admission-status-filter-list">
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'all' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('all')}
                        >
                          All admissions
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Active' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Active')}
                        >
                          Active
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Deactive' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Deactive')}
                        >
                          Deactive
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Paid' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Paid')}
                        >
                          Paid
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Failed' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Failed')}
                        >
                          Failed
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="customer-warning-new-hamburger-help">
            {/* <p className="customer-warning-text">
              <span><i className="fa-solid fa-triangle-exclamation"></i> Update GST details</span> for 520 customers
            </p> */}
            <div className="customer-new">
              <Link to='/add-admission'>
                <button className="oms-add-btn"><i className="fa-solid fa-plus"></i> New</button>
              </Link>
            </div>
            <div className="filter-controls">
              <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
                <i className="fa-solid fa-sliders"></i> Filters
              </button>
            </div>
            <div className="import-hamburger" onClick={toggleImport}>
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </div>
            {isImportOpen && (
              <div className="import-ul">
                <ul>
                  <li>
                    <Link to="/admission-centers-settings" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-location-dot"></i> Manage centers
                    </Link>
                  </li>
                  <li>
                    <Link to="/admission-batches" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-table-cells"></i> Batches
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Link to="/admission-import" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-file-arrow-down"></i> Import
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Link to="/export-admission" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-file-arrow-up"></i> Export list
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li onClick={handleDownloadAdmissionSample}>
                    <i className="fa-solid fa-file-arrow-down"></i> Download Sample Excel
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* <div className="filter-search">
          <div className="filter-search-input">
            <input type="search" placeholder="Search..." value={searchQuery} onChange={handleSearch} />
            <i className="fa fa-search"></i>
          </div>
        </div> */}

        {/* <div className="customer-table">
          {loading ? (
            <SkeletonLoader />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Work Phone</th>
                  <th>GST Number</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {
                  currentCustomers.length > 0 ? (

                    currentCustomers.map((customer, index) => (
                      <tr key={index}>
                        <td>{customer.customerName}</td>
                        <td>{customer.companyName}</td>
                        <td>{customer.email}</td>
                        <td>{customer.phone1}</td>
                        <td>{customer.gstNumber}</td>
                        <td>{new Date(customer.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                        <td>
                          <button onClick={() => handleEdit(customer)} className="edit-btn">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button onClick={() => handleDelete(customer.id)} className="delete-btn" style={{ marginLeft: '8px' }}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center' }}>No data found.</td>
                    </tr>
                  )}
              </tbody>
            </table>
          )}
        </div> */}
        <div className="admission-cards-grid">
        {
          currentCustomers.length > 0 ? (
            currentCustomers.map((customer, index) => (
              <div
                className="ecom-customer-card"
                key={customer.id || index}
                onClick={() => navigate(`/admission-detail/${customer.id}`)}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/admission-detail/${customer.id}`);
                  }
                }}
              >
                {customer.createdAt && (
                  <div
                    className="ecom-customer-time admission-card__time"
                    title="Submitted (India time)"
                  >
                    <Calendar size={12} aria-hidden />
                    <span>{formatAdmissionDateTimeIST(customer.createdAt)}</span>
                  </div>
                )}

                <div className="admission-maindivflex">
                  <div
                    className="ecom-customer-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <select
                      className="ecom-status-select"
                      value={["Active", "Deactive", "Paid", "Failed"].includes(customer.status) ? customer.status : "Deactive"}
                      onChange={(e) => handleStatusChange(e, customer)}
                    >
                      <option value="Active">Active</option>
                      <option value="Deactive">Deactive</option>
                      <option value="Paid">Paid</option>
                      <option value="Failed">Failed</option>
                    </select>

                    <button className="ecom-edit-btn"
                      onClick={() => {
                        handleEdit(customer)
                      }}
                    >
                      <i className="fa-solid fa-pen"></i>
                    </button>

                    <button className="ecom-delete-btn"
                      onClick={() => handleDelete(customer.id)}>
                      <i className="fa-solid fa-trash"></i>
                    </button>
                    {/* <div className="admission-add-btn-wrap">
                      <button ><i className="fa-solid fa-plus"></i></button>
                    </div> */}
                  </div>


                </div>



                {customer.name && (
                  <h2 className="ecom-customer-name">{customer.name}</h2>
                )}

                {customer.mobile && (
                  <div className="ecom-customer-row">
                    <Phone className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.mobile}</span>
                  </div>
                )}

                {customer.email && (
                  <div className="ecom-customer-row">
                    <Mail className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.email}</span>
                  </div>
                )}
                {renderStatusRow(customer)}

                {customer.admission_id && (
                  <div className="ecom-customer-row">
                    <BadgeCheck className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.admission_id}</span>
                  </div>
                )}

                {customer.payment_id && (
                  <div className="ecom-customer-row">
                    <Receipt className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.payment_id}</span>
                  </div>
                )}

                {customer.center && (
                  <div className="ecom-customer-row">
                    <MapPin className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.center}</span>
                  </div>
                )}

                {customer.batch?.name && (
                  <div className="ecom-customer-row" title="Linked via batch_id — schedule lives on the batch">
                    <BadgeCheck className="ecom-customer-icon" strokeWidth={2} />
                    <span>
                      {customer.batch.name}
                      {customer.batch.start_time && customer.batch.end_time
                        ? ` · ${customer.batch.start_time}–${customer.batch.end_time}`
                        : ""}
                    </span>
                  </div>
                )}

              </div>
            ))
          ) : (
            <div className="admission-empty" role="status">
              <p className="admission-empty__title">No admissions yet</p>
              <p className="admission-empty__hint">Try another status filter or add a new admission.</p>
            </div>
          )
        }
        </div>

        {totalPages > 1 && (

          <div className="pagination admission-pagination">
            {/* First Page */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronsLeft size={18} />
            </button>

            {/* Previous Page */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Current Page Number Only */}
            <button className="pagination-btn active">
              {currentPage}
            </button>

            {/* Next Page */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronRight size={18} />
            </button>

            {/* Last Page */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}

      </div>
      <FilterDrawer />
    </>
  );
};

export default Admission;
